import React, {useState, useRef, useCallback, useEffect, Fragment} from 'react'
import {makeStyles} from '@material-ui/core'
import {Close} from '@material-ui/icons'
import {DndContext, useDroppable} from '@dnd-kit/core'
import File from 'assets/svgs/File.svg'
import UploadFile from 'assets/svgs/UploadFile.svg'
import {formatBytes} from 'helpers/fileHelper'
import edfImportType from './style'

const useStyles = makeStyles(edfImportType)
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
]
const MIN_FILE_SIZE = 1024

interface FileDropzoneProps {
  onProcessFile: (fileList: File[]) => void
  fileUploaderId: string
}

export default function FileDropzone({
  onProcessFile,
  fileUploaderId,
}: FileDropzoneProps) {
  const [files, setFiles] = useState<File[]>([])
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const classes = useStyles()

  // dnd-kit droppable (for UI integration with dnd-kit ecosystem)
  const {setNodeRef, isOver} = useDroppable({id: 'file-dropzone'})

  const hasFiles = files.length > 0

  const processFiles = useCallback((files: FileList) => {
    const fileArray = Array.from(files)
    onProcessFile(fileArray)
    if (fileArray[0].name.toLowerCase().endsWith('.edf')) {
      setFiles(fileArray)
    }
  }, [])

  const handleNativeDrop = useCallback(
    (e) => {
      e.preventDefault()
      setIsDraggingOver(false)
      if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files)
    },
    [processFiles],
  )

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingOver(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node))
      setIsDraggingOver(false)
  }

  const handleClick = () => {
    if (!hasFiles) {
      inputRef.current?.click()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      inputRef.current?.click()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) processFiles(e.target.files)
    e.target.value = ''
  }

  const clearAll = () => {
    setFiles([])
    onProcessFile([])
  }

  const active = isDraggingOver || isOver

  useEffect(() => {
    if (fileUploaderId === '') {
      setFiles([])
    }
  }, [fileUploaderId])

  return (
    <DndContext>
      <div
        ref={setNodeRef}
        className={`${classes.container} ${
          active ? classes.activeContainer : ''
        } ${hasFiles ? classes.containerHasFiles : ''}`}
        role='button'
        tabIndex={0}
        onDragOver={handleDragOver}
        onClick={handleClick}
        onDragLeave={handleDragLeave}
        onDrop={handleNativeDrop}
        onKeyDown={handleKeyDown}
      >
        {files.length > 0 ? (
          <div className={classes.fileContainer}>
            <img src={File} alt='file icon' style={{width: 24, height: 24}} />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className={classes.fileName}>{files[0].name}</div>(
              {formatBytes(files[0].size)})
            </div>
            <Close
              onClick={clearAll}
              style={{
                cursor: 'pointer',
              }}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <img
              src={UploadFile}
              alt='upload file icon'
              style={{width: 48, height: 48}}
            />
            <div>Drag and drop files here, or click to upload</div>
          </div>
        )}

        <input
          ref={inputRef}
          type='file'
          accept={ACCEPTED_TYPES.join(',')}
          style={{display: 'none'}}
          onChange={handleInputChange}
        />
      </div>
    </DndContext>
  )
}
